import { useState, FC, useEffect } from 'react';
import { Icon, SubMenuItems } from 'learning-panel-components';
import { translate } from 'translations';
import { useNavigate, useParams } from 'react-router-dom';
import AssignedContentContainer from './assignedContent/assignedContentContainer';
import CareerJourneyContainer from './careerJourneys/careerJourneysContainer';
import RecentlyAddedContainer from './recentlyAdded/recentlyAddedContainer';
import VideoTutorialsContainer from './videoTutorialsPage/videoTutorialsPageContainer';
import OthersMembersTakingContainer from './othersMembersTaking/otherMembersTaking';
import HomeRightSide, { HomeRightSideProps } from '../homeRightSide/homeRightSide';
import styles from './homeTabs.module.scss';
import AllContainer from './all/all';

const HomeTabs: FC<HomeTabsProps> = (props: HomeTabsProps) => {
    const {
        fullWidth,
        setCertificateCourseUuid,
        setIsCustomCourse,
        setVideoTutorialUuid,
        onContactUsClick,
        rightSideData,
        showRightSide,
        isLeanUser,
        showTabs
    } = props;

    const tabs = {
        all: {
            iconName: undefined,
            text: translate('All'),
            activeOnLean: true,
            component: (
                <AllContainer
                    setCertificateCourseUuid={setCertificateCourseUuid}
                    setIsCustomCourse={setIsCustomCourse}
                    onContactUsClick={onContactUsClick}
                    setVideoTutorialUuid={setVideoTutorialUuid}
                    fullWidth={fullWidth}
                />
            )
        },
        'assigned-content': {
            iconName: 'assignedContentIcon',
            text: translate('AssignedContent'),
            activeOnLean: false,
            component: (
                <AssignedContentContainer
                    setCertificateCourseUuid={setCertificateCourseUuid}
                    setIsCustomCourse={setIsCustomCourse}
                    onContactUsClick={onContactUsClick}
                    fullWidth={fullWidth}
                />
            )
        },
        'career-journey': {
            iconName: 'careerJourneyIcon',
            text: translate('CareerJourneys'),
            activeOnLean: true,
            component: <CareerJourneyContainer fullWidth={fullWidth} />
        },
        'recently-added': {
            iconName: 'recentlyAddedIcon',
            text: translate('recentlyAdded'),
            activeOnLean: true,
            component: (
                <RecentlyAddedContainer
                    setCertificateCourseUuid={setCertificateCourseUuid}
                    setIsCustomCourse={setIsCustomCourse}
                    setVideoTutorialUuid={setVideoTutorialUuid}
                    onContactUsClick={onContactUsClick}
                    fullWidth={fullWidth}
                />
            )
        },
        'others-are-taking': {
            iconName: 'otherAreTakingIcon',
            text: translate('OthersAreTaking'),
            activeOnLean: true,
            component: (
                <OthersMembersTakingContainer
                    setCertificateCourseUuid={setCertificateCourseUuid}
                    setIsCustomCourse={setIsCustomCourse}
                    setVideoTutorialUuid={setVideoTutorialUuid}
                    onContactUsClick={onContactUsClick}
                    fullWidth={fullWidth}
                />
            )
        },
        'monthly-videos': {
            iconName: 'monthlyVideosIcon',
            text: translate('BonusVideos'),
            activeOnLean: true,
            component: <VideoTutorialsContainer setVideoTutorialUuid={setVideoTutorialUuid} fullWidth={fullWidth} />
        }
    };

    const leanTabs = {
        all: tabs.all,
        'career-journey': tabs['career-journey'],
        'recently-added': tabs['recently-added'],
        'others-are-taking': tabs['others-are-taking'],
        'monthly-videos': tabs['monthly-videos']
    };

    const { subTab = 'all' } = useParams<RouteParams>();
    const navigate = useNavigate();
    const [userTabs, setUserTabs] = useState<any>(tabs);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(
        SubMenuItems[useParams<RouteParams>()?.subTab || 'all']
    );

    const onSelectSubTab = (item: keyof typeof SubMenuItems) => navigate(`/app/home/${String(item)}`);

    useEffect(() => {
        if (isLeanUser) {
            setUserTabs(leanTabs);
        } else {
            setUserTabs(tabs);
        }
    }, [isLeanUser]);

    return (
        <div className={styles['home-tabs-wrapper']}>
            {showTabs && (
                <div className={styles.tabs}>
                    {Object.keys(userTabs).map((key, index) => {
                        const tab = userTabs[key as keyof typeof SubMenuItems];
                        return (
                            <div
                                key={index}
                                className={subTab === key ? styles['active-tab'] : styles.tab}
                                onClick={() => {
                                    onSelectSubTab(key as keyof typeof SubMenuItems);
                                    setActiveTabIndex(index);
                                }}
                            >
                                {tab.iconName && <Icon name={tab.iconName} className={styles.icon} />}
                                <div className={styles.text}>{tab.text}</div>
                                {subTab !== key &&
                                    activeTabIndex !== index + 1 &&
                                    index + 1 !== Object.keys(userTabs).length && <hr />}
                            </div>
                        );
                    })}
                </div>
            )}
            <div className={styles['home-tab-content']}>
                {tabs[subTab as keyof typeof SubMenuItems].component}
                {showRightSide && <HomeRightSide {...rightSideData} />}
            </div>
        </div>
    );
};

interface HomeTabsProps {
    fullWidth?: boolean;
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    setVideoTutorialUuid: Function;
    onContactUsClick: Function;
    rightSideData: HomeRightSideProps;
    showRightSide: boolean;
    isLeanUser: boolean;
    showTabs: boolean;
}

type RouteParams = {
    subTab: keyof typeof SubMenuItems;
};

export default HomeTabs;
